import { useSelector } from 'react-redux';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { TYPES_MAP_FROM_RATING_SERVICE } from '../../constants/partners';
import { ISravniRating } from '../../interfaces/ratings';
import { getSummaryRatings, IRatingsParams } from '../../services/ratings';
import { IGlobalState } from '../index';

export interface IState {
  banks: ISravniRating[];
  mfi: ISravniRating[];
  insuranceCompany: ISravniRating[];
  error: boolean;
}

const initialState: IState = {
  banks: [],
  mfi: [],
  insuranceCompany: [],
  error: false,
};

export const fetchRatingsByType = createAsyncThunk('rating/fetch', async (params: IRatingsParams) => {
  const items = await getSummaryRatings(params);

  return {
    items,
    type: TYPES_MAP_FROM_RATING_SERVICE[params.OrganizationType],
  };
});

const ratingSlice = createSlice({
  name: 'rating',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchRatingsByType.fulfilled, (state, { payload }) => {
      state[payload.type] = payload.items;
    });
    builder.addCase(fetchRatingsByType.rejected, (state, { payload }) => {
      state.error = !!payload;
    });
  },
});

const { reducer } = ratingSlice;

export default reducer;

export const banksRatingsSelector = (state: IGlobalState): ISravniRating[] => state.ratings.banks;
export const mfiRatingsSelector = (state: IGlobalState): ISravniRating[] => state.ratings.mfi;
export const insuranceCompaniesRatingsSelector = (state: IGlobalState): ISravniRating[] =>
  state.ratings.insuranceCompany;
export const ratingsSelector = (state: IGlobalState): IState => state.ratings;

export function useBanksRatings(): IState['banks'] {
  return useSelector<IGlobalState, IState['banks']>(banksRatingsSelector);
}

export function useMfiRatings(): IState['banks'] {
  return useSelector<IGlobalState, IState['mfi']>(mfiRatingsSelector);
}

export function useInsuranceCompaniesRatings(): IState['banks'] {
  return useSelector<IGlobalState, IState['insuranceCompany']>(insuranceCompaniesRatingsSelector);
}

export function useRatings(): IState {
  return useSelector<IGlobalState, IState>(ratingsSelector);
}
